/**
 * Analytics read hook.
 *
 * Its own query-key namespace: analytics is a reporting view, not live operator
 * work, so a queue refresh or a Firestore hint must not churn it.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import {
  buildAlertAnalyticsParams,
  getAlertAnalytics,
} from "@/alerts/api/requests/alerts-analytics.requests.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import type {
  AlertAnalyticsFilterState,
  AlertAnalyticsResponse,
} from "@/alerts/types/alerts-analytics.ts";

export const useAlertAnalytics = (state: AlertAnalyticsFilterState | null) => {
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);

  return useQuery<AlertAnalyticsResponse>({
    // Keyed off the serialized params, so a mode switch is a different query
    // rather than the same one carrying stale incompatible state.
    queryKey: [
      QUERY_KEYS.ALERTS_ANALYTICS,
      organizationId,
      state ? buildAlertAnalyticsParams(state) : null,
    ],
    queryFn: ({ signal }) => getAlertAnalytics(state as AlertAnalyticsFilterState, signal),
    enabled: state != null,
    // Hold the last good report through a background refresh.
    placeholderData: keepPreviousData,
    // A rejected PERIOD is not transient — retrying just repeats the rejection.
    retry: false,
  });
};
